import { httpBatchLink, createTRPCClient, loggerLink, TRPCClient } from '@trpc/client'

import { deserialize } from '@authy/lib-common/utils/misc'
import type { AppRouter } from '@authy/server/routes'

import { clientAppState } from '@/appState'
import { loadDebugOptions } from '@/app'
import { env } from '@/env'

export const client: TRPCClient<AppRouter> = createTRPCClient<AppRouter>({
    links: [
        loggerLink({
            enabled: () => clientAppState.isDevelopment && !!loadDebugOptions().logTRPCCalls,
        }),
        loggerLink({
            enabled: () => true,
            logger: (data) => {
                if (data.direction === 'down') {
                    const breadcrumbData: Record<string, any> = {
                        id: data.id,
                        type: data.type,
                        path: data.path,
                        input: data.input,
                        elapsedMs: data.elapsedMs,
                        method: 'POST',
                    }

                    if (!(data.result instanceof Error)) {
                        const response: any = data.result.context?.response

                        breadcrumbData.url = response?.url
                        breadcrumbData.status_code = response?.status
                        breadcrumbData.reason = response?.statusText
                        breadcrumbData.result = data.result.result.data
                    }

                    clientAppState.sentry?.addBreadcrumb({
                        category: 'trpc',
                        type: 'http',
                        level: 'info',
                        data: breadcrumbData,
                        timestamp: Date.now() / 1000,
                    })
                }
            },
        }),
        httpBatchLink({
            transformer: {
                input: {
                    serialize: (object) => object,
                    deserialize: (object) => object,
                },
                output: {
                    serialize: (object) => object,
                    deserialize: (object) => {
                        if (typeof object === 'string') {
                            return deserialize(object)
                        }
                        return object
                    },
                },
            },
            url: `${env.VITE_PRODUCT_ENDPOINT}/trpc`,
            async fetch(url, options) {
                const response = await fetch(url, options)

                if (response.status === 400) {
                    try {
                        // we need to clone the response to be able to read it and still return it
                        const resp = response.clone()
                        const body = await resp.json()
                        const error = JSON.parse(Array.isArray(body) ? body.find((b) => b?.error)?.error : body.error)
                        if (error.cause === 'InvalidToken') {
                            // clear the cache and refresh the page
                            clientAppState?.auth?.logout({ onlyClearSession: true })
                            window.location.reload()
                            await new Promise((resolve) => setTimeout(resolve, 5000))
                        }
                    } catch (error) {
                        // do nothing and let the trpc handle the error
                    }
                }

                return response
            },
            async headers() {
                const headers: Record<string, string> = {}

                const token = await clientAppState?.auth?.getAccessToken?.()
                const clientId = clientAppState?.auth?.clientId

                if (token) {
                    headers['Authorization'] = `Bearer ${clientId}:${token}`
                }

                headers['Accept-Language'] = clientAppState.language

                return headers
            },
        }),
    ],
})
